
import { API_ENDPOINTS } from '@/src/constants';
import { useAuth, useNetwork } from '@/src/contexts';
import { fireblocksClient } from '@/src/helpers';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  FireblocksQueryKeys,
  FireblocksResponse,
  PaginatedAddressResponse,
  RequestError,
  VaultAccount,
  VaultAccountsPagedResponse,
  VaultsApiGetPagedVaultAccountsRequest,
  VaultsApiGetVaultAccountAssetAddressesPaginatedRequest,
} from './fireblocks.types';

/**
 * Gets all vault accounts in your workspace. This endpoint returns a limited amount of results with a quick response time.
 * @returns Vault accounts
 */
export const useFireblocksVaultAccounts = (params?: VaultsApiGetPagedVaultAccountsRequest) => {
  return useQuery({
    queryKey: [FireblocksQueryKeys.VaultAccounts, params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params?.namePrefix) searchParams.append('namePrefix', params.namePrefix);
      if (params?.nameSuffix) searchParams.append('nameSuffix', params.nameSuffix);
      if (params?.minAmountThreshold)
        searchParams.append('minAmountThreshold', params.minAmountThreshold.toString());
      if (params?.assetId) searchParams.append('assetId', params.assetId);
      if (params?.orderBy) searchParams.append('orderBy', params.orderBy);
      if (params?.before) searchParams.append('before', params.before);
      if (params?.after) searchParams.append('after', params.after);
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.tagIds) searchParams.append('tagIds', params.tagIds.join(','));

      const url = `${API_ENDPOINTS.FIREBLOCKS_VAULT_ACCOUNTS}?${searchParams}`;

      return fireblocksClient
        .get<FireblocksResponse<VaultAccountsPagedResponse>>(url)
        .then((res) => res.data);
    },
  });
};

export const useFireblocksVaultAccount = () => {
  const { user } = useAuth();

  // Create vault account object directly from user data
  // This avoids the unnecessary API call to query vault accounts by namePrefix
  const vaultAccount = useMemo(() => {
    if (!user?.vault_id) return undefined;

    return {
      id: user.vault_id,
      name: user.email,
    } as VaultAccount;
  }, [user?.vault_id, user?.email]);

  return {
    data: vaultAccount,
    isLoading: false, // No API call needed since we have vault_id from auth
  };
};

/**
 * Get a single vault account by ID with all assets from Fireblocks API
 * This fetches directly by vault ID which is more efficient than searching by namePrefix
 */
export const useFireblocksVaultAccountById = (vaultAccountId?: string) => {
  const { isTestnet } = useNetwork();

  return useQuery({
    queryKey: [FireblocksQueryKeys.VaultAccounts, vaultAccountId, isTestnet ? 'testnet' : 'mainnet'],
    queryFn: async () => {
      // Fetch vault account by ID - endpoint format: /api/fireblocks/vault-accounts/{id}/
      const url = `${API_ENDPOINTS.FIREBLOCKS_VAULT_ACCOUNTS}${vaultAccountId}/`;

      return fireblocksClient
        .get<VaultAccount>(url)
        .then((res) => res.data);
    },
    enabled: !!vaultAccountId,
    staleTime: 0, // Always refetch on network change since query key includes network
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false, // Don't refetch when user returns to tab
    refetchOnMount: true, // Refetch when component mounts
  });
};

export const useFireblocksVaultAccountAssetAddresses = (
  params: VaultsApiGetVaultAccountAssetAddressesPaginatedRequest,
  opts?: Omit<
    UseQueryOptions<FireblocksResponse<PaginatedAddressResponse>, RequestError>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    queryKey: [
      FireblocksQueryKeys.VaultAccountAssetAddresses,
      params.vaultAccountId,
      params.assetId,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      // Required parameters
      searchParams.append('vaultAccountId', params.vaultAccountId);
      searchParams.append('assetId', params.assetId);

      // Optional parameters
      if (params.before) searchParams.append('before', params.before);
      if (params.after) searchParams.append('after', params.after);
      if (params.limit) searchParams.append('limit', params.limit.toString());

      const url = `${API_ENDPOINTS.FIREBLOCKS_VAULT_ACCOUNT_ASSET_ADDRESSES}?${searchParams}`;

      const response =
        await fireblocksClient.get<FireblocksResponse<PaginatedAddressResponse>>(url);
      return response.data;
    },
    ...opts,
  });
};

/**
 * Get the vault account asset address at a given index
 * @param index - The index of the address to get, defaults to 0
 * @returns
 */
export const useFireblockVaultAccountAssetAddressAtIndex = (index = 0) => {
  const { user } = useAuth();
  const { data: vaultAccount } = useFireblocksVaultAccount();

  // Create address object directly from user data
  // This avoids the unnecessary API call to query vault account asset addresses
  const vaultAccountAssetAddress = useMemo(() => {
    if (!user?.xrp_address || index !== 0) return undefined;

    return {
      address: user.xrp_address,
    };
  }, [user?.xrp_address, index]);

  return useMemo(() => {
    return {
      vaultAccountAssetAddress,
      vaultAccount,
      isLoading: false, // No API call needed since we have xrp_address from auth
    };
  }, [vaultAccountAssetAddress, vaultAccount]);
};
